import type React from 'react';
import { useCallback } from 'react';
import { range } from 'lodash-es';

import { Box, Divider, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { PetSection } from '../../../../models/index';
import Field from '../../../../models/Field';
import PetInformation from './PetInformation';
import { PETS } from '../../../../constants/index';
import { useIndexedSection } from '../../../../hooks';

type Props = {
  maximumPets: number;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PetSection) => void;
  section: PetSection;
};

PetList.defaultProps = {
  maximumPets: 5,
};

export default function PetList({ maximumPets, setSectionValues, setSection, section }: Props): JSX.Element {
  const initializePetSection = useCallback((petSection: PetSection) => {
    if (petSection.getTotalIndexes() === 0) {
      petSection.addOneIndex();
    }
  }, []);

  const { onAddOne: addOneIndex, onDeleteOne: onRemovePet } = useIndexedSection({
    section,
    setSection,
    SectionClass: PetSection,
    onIndexAdded: initializePetSection,
  });

  function onFieldChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { fieldName } = event.currentTarget.dataset;
    const newValue = event.currentTarget.value;

    setSectionValues({ [fieldName]: newValue });

    // If the field is the pet type field, clear out fields not relevant to the selected type
    const field = section.getField(fieldName);
    const { fieldNameRoot, index } = field.getFieldNameRootAndIndex();
    if (fieldNameRoot === PETS.FIELD_NAME.TYPE) {
      if (PetSection.isOtherType(newValue)) {
        section.setFieldValue(Field.getIndexedFieldName(PETS.FIELD_NAME.BREED, index), '');
        section.setFieldValue(Field.getIndexedFieldName(PETS.FIELD_NAME.GENDER, index), '');
      }
    }
  }

  /*
  Renders all visible people. Pets are considered "visible" if their field enteredValues aren't null
  */
  function renderPet(index: number): React.ReactNode {
    const breedField = section.getIndexedFieldGroup(PETS.FIELD_NAME.BREED)[index];
    const genderField = section.getIndexedFieldGroup(PETS.FIELD_NAME.GENDER)[index];
    const licenseField = section.getIndexedFieldGroup(PETS.FIELD_NAME.LICENSE_NUMBER)[index];
    const nameField = section.getIndexedFieldGroup(PETS.FIELD_NAME.NAME)[index];
    const typeField = section.getIndexedFieldGroup(PETS.FIELD_NAME.TYPE)[index];
    const weightField = section.getIndexedFieldGroup(PETS.FIELD_NAME.WEIGHT)[index];

    const props = {
      breedField,
      genderField,
      licenseField,
      nameField,
      typeField,
      weightField,
      breedValue: breedField.enteredValue,
      genderValue: genderField.enteredValue,
      licenseValue: licenseField.enteredValue,
      nameValue: nameField.enteredValue,
      typeValue: typeField.enteredValue,
      weightValue: weightField.enteredValue,
      typeHasError: typeField.hasError,
      genderHasError: genderField.hasError,
      weightHasError: weightField.hasError,
    };

    /*
    *Note that enteredValues are being passed in separately from their respective fields.
    This is done so that the PetInformation can be implemented as a PureComponent. Since
    the fields object references that don't change between updates, updating
    their enteredValues do not trigger updates.

    Passing the enteredValues separately achieves this.
    */
    return <PetInformation {...props} index={index} onFieldChange={onFieldChange} onRemovePet={onRemovePet} />;
  }

  const totalPets = section.getTotalIndexes();

  if (totalPets == null) {
    // This condition is true on first render before the Section object
    // is transitioned to a PetSection object in the constructor.
    return null;
  }

  return (
    <>
      <Paragraph textStyle="body-lg-bold" css={{ marginBottom: 'layout.tight' }}>
        Tell us about their pets
      </Paragraph>
      {range(0, totalPets).map((index: number) => (
        <div data-test="pet-container" key={index}>
          {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
          {renderPet(index)}
        </div>
      ))}
      <Box css={{ marginTop: 'layout.tight' }}>
        {totalPets < maximumPets ? (
          <TextButton data-test="add-another" icon={<IconPlusFilled />} onClick={addOneIndex}>
            Add another pet
          </TextButton>
        ) : (
          maximumPets > 1 && (
            <Paragraph data-test="maximum-reached">You’ve reached the maximum of {`${maximumPets} pets`}</Paragraph>
          )
        )}
      </Box>
    </>
  );
}
